import React, { FC, forwardRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import styled from 'styled-components';

import LogoutIcon from '@mui/icons-material/Logout';
import LockIcon from '@mui/icons-material/Lock';
import StopIcon from '@mui/icons-material/Stop';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { hasRole, platformSelectors, useDeepCompareMemo, userRolesMap } from '@clatter/platform';
import { DropDown, IconButton } from '@clatter/ui';

import { routesNames } from '../../../routes';
import { themeUISelectors } from '../../../store/UI/theme.slice';
import {
  addDismissedSection,
  assetManagerUISelectors,
  authSelectors,
  impersonateSelectors,
  ngAppSelectors,
  removeDismissedSection,
  setSearchQuery,
  setSelectedTheme,
  stopImpersonating,
} from '../../../store';
import { NgDivider } from '../NgDivider/NgDivider';
import { SearchInput } from '../SearchInput/SearchInput';
import { DropdownSelect } from '../DropdownSelect/DropdownSelect';
import { UserProfileIcon } from '../../UserProfileIcon/UserProfileIcon';
import clatterLogo from '../../../../assets/logos/clatter_logo.png';
import { Tooltip } from '@mui/material';
import { AccessGuard } from '../../AccessGuard/AccessGuard';

const StyledLogoContainer = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  flex: 1;

  .clickable {
    cursor: pointer;
  }

  img {
    cursor: pointer;
    max-height: 37px;
    flex-shrink: 0;
  }

  .co-brand-logo {
    max-height: 30px;
  }
`;

const StyledNavBar = styled.nav`
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99;
  padding: 0.7rem 1.25rem;
  background-color: ${({ theme }) => theme?.palette?.white};
  color: ${({ theme }) => theme?.palette?.text};
  border-bottom: 1px solid ${({ theme }) => theme?.palette?.accent};

  .column {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex: 1;
  }

  .align-right {
    justify-content: flex-end;
  }
`;

const SearchBar = () => {
  const dispatch = useDispatch();
  const searchQuery = useSelector(assetManagerUISelectors.getSearchQuery);

  const handleSearchChange = (value: string) => {
    dispatch(setSearchQuery(value));
  };

  return (
    <SearchInput
      value={searchQuery}
      placeholder="Search Templates, Assets and Collections"
      onSearch={handleSearchChange}
    />
  );
};

const SyledImpersonated = styled.div`
  display: flex;
  height: 100%;
  align-items: center;

  gap: ${({ theme }) => theme.spacing.small};

  p {
    margin: 0;
  }
`;

const ImpersonatedInfo: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const impersonatedUser = useSelector(impersonateSelectors.getImpersonatedUser);

  if (!impersonatedUser) {
    return null;
  }

  return (
    <SyledImpersonated>
      <p>
        Impersonating: <strong>{impersonatedUser?.email} </strong>
      </p>
      {/* @ts-expect-error */}
      <IconButton
        onClick={(e: any) => {
          e.stopPropagation();
          navigate('/');
          dispatch(stopImpersonating());
        }}
      >
        <Tooltip title="Stop">
          <StopIcon />
        </Tooltip>
      </IconButton>
    </SyledImpersonated>
  );
};

const HOME_WELCOME_SECTION_KEY = 'home-welcome';

export const NavBar = forwardRef<HTMLDivElement, any>((props, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;
  const impersonatedUser = useSelector(impersonateSelectors.getImpersonatedUser);
  const toolInfo = useSelector(platformSelectors.toolInfo.selectToolInfo);
  const isWelcomeDismissed = useSelector((state) => ngAppSelectors.isSectionDismissed(state, HOME_WELCOME_SECTION_KEY));
  const appVersion = process.env?.['NX_PUBLIC_APP_VERSION'] || '';

  const [isProfileDropDownOpen, setProfileDropDownOpen] = useState(false);

  const toolsList = useSelector(platformSelectors.tools.selectAllTools);
  const activeToolItem = toolsList.find((tool: any) => tool?.url?.includes(window.location.origin));
  const selectedTheme = useSelector(themeUISelectors.getSelectedTheme) as SingleValue<IDropdownSelectOptionItem>;
  const themesListOptions = useSelector(themeUISelectors.getThemesOptionsList);

  const brandLogo = toolInfo?.data?.companyLogo?.location || clatterLogo;
  const isClatterAdmin = hasRole(userRolesMap.clatterAdmin, activeUser);

  const showThemePicker = false;

  //region METHODS
  const onPasswordChangeClick = async () => {
    try {
      setProfileDropDownOpen(false);
      console.log('Password change clicked');
      //@todo
    } catch (error) {
      console.log('Password change error');
      //@todo
    }
  };

  const onToggleWelcome = () => {
    dispatch(
      isWelcomeDismissed
        ? removeDismissedSection(HOME_WELCOME_SECTION_KEY)
        : addDismissedSection(HOME_WELCOME_SECTION_KEY),
    );
    // setProfileDropDownOpen(false);
  };

  const onProfileClick = () => {
    setProfileDropDownOpen(false);
    navigate(routesNames.userProfile);
  };

  const onLogoutClick = () => {
    setProfileDropDownOpen(false);
    navigate(routesNames.logout);
  };

  const onImpersonateClick = () => {
    setProfileDropDownOpen(false);
    navigate(routesNames.impersonate);
  };

  const handleThemeChange = (data: any) => {
    dispatch(setSelectedTheme(data));
  };

  const handleLogoClick = () => {
    navigate('/');
  };
  //endregion

  const profileDropDownOptions = useDeepCompareMemo(() => {
    return [
      {
        name: 'Profile',
        icon: <AccountCircleIcon />,
        clickEvent: onProfileClick,
      },
      (AccessGuard.check({ allowedRoles: [userRolesMap.clatterAdmin], user: activeUser }) || impersonatedUser) && {
        name: 'Impersonate',
        icon: <SyncAltIcon />,
        clickEvent: onImpersonateClick,
      },
      {
        name: 'Change Password',
        icon: <LockIcon />,
        clickEvent: onPasswordChangeClick,
      },
      {
        name: isWelcomeDismissed ? 'Show Welcome' : 'Hide Welcome',
        icon: isWelcomeDismissed ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />,
        clickEvent: onToggleWelcome,
      },
      {
        name: 'Logout',
        icon: <LogoutIcon />,
        clickEvent: onLogoutClick,
      },
    ].filter((item) => item);
  }, [isClatterAdmin, impersonatedUser, activeUser, isWelcomeDismissed]);

  //region EFFECTS
  const toggleUserProfileDropdown = () => {
    setProfileDropDownOpen((prev) => !prev);
  };
  //endregion

  return (
    <StyledNavBar ref={ref} {...props}>
      <StyledLogoContainer>
        <DropDown
          hideActive
          options={toolsList as any}
          value={activeToolItem}
          noValueLabel="Select a Tool"
          minDropdownWidth="250px"
          triggerComponent={(<img src={clatterLogo} alt="clatter logo" />) as any}
        />

        <NgDivider vertical />

        <img className="co-brand-logo" src={brandLogo} onClick={() => handleLogoClick()} alt="brand logo" />
      </StyledLogoContainer>

      <div className="column align-right">
        {showThemePicker && (
          <div style={{ minWidth: '130px' }}>
            <DropdownSelect value={selectedTheme} options={themesListOptions} onChange={handleThemeChange} />
          </div>
        )}

        <ImpersonatedInfo />

        <UserProfileIcon
          testId="user-circle"
          appVersion={appVersion}
          user={activeUser}
          onClose={() => setProfileDropDownOpen(false)}
          isOpen={isProfileDropDownOpen}
          dropdownOptions={profileDropDownOptions as any}
          handleUserIconClick={toggleUserProfileDropdown}
        />
      </div>
    </StyledNavBar>
  );
});
